/**
 * SUB-2.7 · Who sees the account picker, and what it offers them.
 *
 * FR12: an Agency Admin sees their parent account and its sub-accounts, and
 * nothing else. FR65: a role they hold in an unrelated account is preserved.
 * FR67: archived sub-accounts are excluded.
 *
 * ⚠️ THE LIST ITSELF IS NOT BUILT HERE, AND NOT IN THE FRONTEND AT ALL.
 * TenantContext.fetchUserTenants resolves it from `users.tenant_id` unioned with
 * `user_roles.tenant_id`, then filters archived accounts in the database. SUB-2.6
 * (`20260812j`) provisions a real `user_roles` row in every sub-account of the
 * parent, marked `granted_by_parent_tenant_id`, and keeps it reconciled as the
 * hierarchy changes. So all three ACs are already answered by the grant table
 * before this module is reached, and this module only decides visibility.
 *
 * Two decisions worth stating, because the obvious alternative to each is wrong.
 *
 * 1. THE LIST IS NOT `accessible_tenant_ids()`. That function answers "what is in
 *    this ACCOUNT's subtree", never "what may this USER reach": it carries no role
 *    gate, so it returns the same array for an Agency Admin and for a Sales Rep in
 *    the same parent account. Driving the picker from it would disclose every
 *    client account to every user of the parent, which is exactly what FR19 and
 *    SUB-2.12 forbid. It would also drop the FR65 accounts, which are outside the
 *    subtree by definition, and could list a sub-account before the reconciler had
 *    granted the user any authority in it. `user_roles` is the server's own record
 *    of what was granted, so reading it is not a second definition of membership;
 *    it is the definition, one step downstream of the resolver that populates it.
 *
 * 2. THE MARKER IS "HOLDS AGENCY ADMIN ANYWHERE", NOT "IN THE CURRENT TENANT".
 *    `check_user_role('Agency Admin')` resolves against the caller's current
 *    tenant. The grant sits on the parent, so the moment SUB-2.8 switches an
 *    Agency Admin into a client account that check goes false and the picker would
 *    vanish, stranding them inside the sub-account with no way back.
 *
 * None of this is enforcement. It decides what is rendered; the database decides
 * what is permitted, and SUB-2.8 bounds the switch server-side.
 */

/** The role name SUB-2.5 (`20260812g`) established in `roles`. */
export const AGENCY_ADMIN_ROLE_NAME = 'Agency Admin';

/**
 * One row carrying an embedded role name.
 *
 * Usually a `user_roles` row as the picker's query returns it. Since 2026-08-13 it
 * is also used for the LEGACY shape, a single synthetic row built from
 * `users.role_id`'s resolved role, which is why `tenant_id` is optional rather than
 * merely lenient: that source has no per-row tenant.
 */
export interface UserRoleRow {
  tenant_id?: string | null;
  /** PostgREST returns an embed as an object or a single-element array. Both occur. */
  roles?: { name?: string | null } | { name?: string | null }[] | null;
}

export interface TenantPickerViewer {
  /** Superadmin, as the app has always measured it. */
  hasSystemOverride: boolean;
  /** Holds the Agency Admin role in at least one account. */
  isAgencyAdmin: boolean;
  /** How many non-archived accounts the viewer reaches. */
  tenantCount: number;
}

/**
 * Does this user hold the Agency Admin role in any account?
 *
 * Exact name match. A near-miss spelling is a different role, and treating it as
 * this one would be the name-keyed guessing that let F-8 hide behind a fourth
 * spelling of "is this user an admin".
 *
 * ⚠️ THE CALLER MUST ASK IT ABOUT BOTH ROLE SOURCES. This function answers only
 * about the rows it is given, and `TenantContext` used to give it `user_roles`
 * alone. An Agency Admin whose role lives in the legacy `users.role_id` column then
 * read as "not an Agency Admin", losing them the picker (FR12) and the
 * /app/sub-accounts entry point (FR63). SUB-1.11 measured that column as the role
 * source for 42 of 50 active users, and the server has honoured both paths since
 * 20260812j (`agency_grant_basis_holds`, and SUB-3.1's `is_agency_admin_of`).
 * Pass the legacy role as a single synthetic row: `[{ roles: legacyRole }]`.
 */
export function holdsAgencyAdminRole(rows: UserRoleRow[] | null | undefined): boolean {
  if (!rows || rows.length === 0) return false;

  return rows.some((row) => {
    const embed = row?.roles;
    if (!embed) return false;
    const named = Array.isArray(embed) ? embed : [embed];
    return named.some((role) => role?.name === AGENCY_ADMIN_ROLE_NAME);
  });
}

/**
 * FR12. Show the picker to superadmins, as before, and to Agency Admins, who are
 * the only other users with more than one account to be in.
 *
 * The `tenantCount > 1` term is the pre-existing rule and is kept for both: a
 * picker offering one account is a control that does nothing. It is also what
 * keeps FR19 true for a sub-account's own users, who reach exactly one account.
 */
export function canSeeTenantPicker(viewer: TenantPickerViewer): boolean {
  if (viewer.tenantCount <= 1) return false;
  return viewer.hasSystemOverride || viewer.isAgencyAdmin;
}

/**
 * Creating and cloning accounts are superadmin actions. This is a separate
 * question from seeing the picker, and it must stay separate: the two actions
 * live inside the dropdown, so widening who opens it would otherwise widen who
 * is offered them.
 *
 * ⚠️ CORRECTED 2026-08-12 (SUB-2.11). This used to say the two were "refused
 * server-side by `create_tenant`'s `system.override` gate". Read from `pg_proc`,
 * that gate does not exist: `create_tenant` is SECURITY DEFINER with EXECUTE to
 * `authenticated`, and only the CLONE path checks Superadmin. The blank-creation
 * path validates its arguments and inserts. So this function is currently the
 * only thing between an ordinary user and a new tenant, which is a UI control
 * doing a server's job. Recorded as F-18 in security-findings.md under
 * `## SUB-2.11`; the fix is a migration and needs the database slot.
 */
export function canManageTenants(viewer: TenantPickerViewer): boolean {
  return viewer.hasSystemOverride;
}
